import { useMemo, useState } from 'react';
import DataTable, { Column } from '@/components/common/DataTable';
import type { Member } from '@/types/member';

const tierStyles: Record<string, string> = {
  Starter: 'bg-background-200 text-foreground-700',
  Pro: 'bg-primary-100 text-primary-700',
  Elite: 'bg-accent-100 text-accent-800',
};

interface MemberTableProps {
  data: Member[];
  onSelect: (member: Member) => void;
}

export default function MemberTable({ data, onSelect }: MemberTableProps) {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const columns: Column<Member>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Member',
        title: 'Member Info',
        sortable: true,
        sortValue: (m) => m.name,
        render: (member) => (
          <div className="flex items-center gap-3">
            <span
              className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full text-xs font-medium shadow-2xs ${
                tierStyles[member.tier] ?? 'bg-background-200 text-foreground-700'
              }`}
            >
              {member.name
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)}
            </span>
            <div className="min-w-0">
              <p className="font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                {member.name}
              </p>
              <p className="text-xs text-foreground-500 truncate">{member.email}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'platform',
        header: 'Platform',
        title: 'Platform',
        sortable: true,
        render: (member) => (
          <span className="inline-flex items-center gap-1.5 text-xs font-normal text-foreground-700 whitespace-nowrap bg-background-100/90 px-2.5 py-1 rounded-lg border border-background-200/80 shadow-2xs">
            <i className="ri-plug-line text-primary-500"></i>
            {member.platform}
          </span>
        ),
      },
      {
        key: 'tier',
        header: 'Tier',
        title: 'Tier Level',
        sortable: true,
        render: (member) => (
          <span
            className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap shadow-2xs ${
              tierStyles[member.tier] ?? 'bg-background-200 text-foreground-700'
            }`}
          >
            {member.tier}
          </span>
        ),
      },
      {
        key: 'balance',
        header: 'Balance',
        title: 'Points Balance',
        sortable: true,
        align: 'right',
        sortValue: (m) => m.balance,
        render: (member) => (
          <span className="font-semibold text-foreground-900 tabular-nums">
            {member.balance.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'lifetime',
        header: 'Lifetime',
        title: 'Lifetime Points',
        sortable: true,
        align: 'right',
        sortValue: (m) => m.lifetime,
        render: (member) => (
          <span className="text-foreground-600 font-normal tabular-nums">
            {member.lifetime.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        title: 'Account Status',
        sortable: true,
        render: (member) => (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-normal px-2.5 py-0.5 rounded-full whitespace-nowrap ${
              member.status === 'active'
                ? 'bg-accent-50 text-accent-700 border border-accent-200/60'
                : 'bg-background-200/70 text-foreground-500 border border-background-300/60'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                member.status === 'active' ? 'bg-accent-500' : 'bg-foreground-400'
              }`}
            ></span>
            {member.status === 'active' ? 'Active' : 'Idle'}
          </span>
        ),
      },
      {
        key: 'joined',
        header: 'Joined',
        title: 'Date Joined',
        sortable: true,
        align: 'right',
        render: (member) => (
          <span className="text-foreground-500 text-xs whitespace-nowrap">
            {member.joined}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        title: 'Actions',
        align: 'right',
        hideable: false,
        width: '48px',
        render: (member) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(member);
            }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors ml-auto cursor-pointer"
            title="View member details"
            aria-label={`View details for ${member.name}`}
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        ),
      },
    ],
    [onSelect]
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      getRowId={(m) => m.id}
      selectable={true}
      selectedIds={selectedIds}
      onSelectionChange={(ids) => setSelectedIds(ids)}
      onRowClick={onSelect}
      columnVisibility={true}
      exportable={true}
      exportFilename="members-export.csv"
      pageSize={8}
      pageSizeOptions={[8, 16, 32, 50]}
      minWidth="920px"
      emptyIcon="ri-user-search-line"
      emptyTitle="No members found"
      emptyDescription="Try adjusting your search terms or filters to find what you are looking for."
      bulkActions={(selectedRows, clear) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              alert(`Action triggered for ${selectedRows.length} members!`);
              clear();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 text-xs font-bold transition-all shadow-2xs cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
            <span>Message ({selectedRows.length})</span>
          </button>
        </div>
      )}
    />
  );
}
